import { useMemo } from 'react';

import {
  AIR_GROUND_COLOR,
  AVIA_SHARE_COLOR,
  getConicGradient,
  MAINTENANCE_COLOR,
  NO_CREW_COLOR,
  NON_CASE_COLOR,
  ON_CASE_COLOR,
  WAITING_FOR_CASE_COLOR,
} from '#entities';

import { useQuerySimpleStatistics } from '../api';

import type { StatisticsDataType } from '#entities';

export const useStatisticsData = () => {
  const { data: statistics } = useQuerySimpleStatistics();

  const statisticsData = useMemo<StatisticsDataType | null>(() => {
    if (!statistics) {
      return null;
    }

    const {
      casesStatistics: { allCases, airGround, tmdxShare },
      aviationStatistics: {
        crewOnCaseHours,
        crewOnNonCaseHours,
        crewTotalHours,
        crewWaitingForCaseHours,
        maintenancesHours,
        noCrewHours,
        days,
      },
    } = statistics;

    const airGroundShare = Math.round((airGround / allCases) * 100);
    const groundShare = 100 - airGroundShare;
    const tmdxSharePercents = Math.round((tmdxShare / airGround) * 100);

    const tmdxWaitingForCase = Math.round(
      (crewWaitingForCaseHours / crewTotalHours) * 100,
    );
    const tmdxOnCase = Math.round((crewOnCaseHours / crewTotalHours) * 100);
    const tmdxNonCase = Math.round((crewOnNonCaseHours / crewTotalHours) * 100);

    const unavailableHours = maintenancesHours + noCrewHours || 1;
    const tmdxNoCrew = Math.round((noCrewHours / unavailableHours) * 100);
    const tmdxMaintenance = Math.round(
      (maintenancesHours / unavailableHours) * 100,
    );

    const tmdxAirGround =
      AIR_GROUND_COLOR.find(i => i.minPercents <= groundShare) ||
      AIR_GROUND_COLOR[0];
    const tmdxShareColors =
      AVIA_SHARE_COLOR.find(i => i.minPercents <= tmdxSharePercents) ||
      AVIA_SHARE_COLOR[0];
    const tmdxWaitingForCaseColors =
      WAITING_FOR_CASE_COLOR.find(i => i.minPercents <= tmdxWaitingForCase) ||
      WAITING_FOR_CASE_COLOR[0];
    const tmdxOnCaseColors =
      ON_CASE_COLOR.find(i => i.minPercents <= tmdxOnCase) || ON_CASE_COLOR[0];
    const tmdxNonCaseColors =
      NON_CASE_COLOR.find(i => i.minPercents <= tmdxNonCase) ||
      NON_CASE_COLOR[0];
    const tmdxNoCrewColors =
      NO_CREW_COLOR.find(i => i.minPercents <= tmdxNoCrew) || NO_CREW_COLOR[0];
    const tmdxMaintenanceColors =
      MAINTENANCE_COLOR.find(i => i.minPercents <= tmdxMaintenance) ||
      MAINTENANCE_COLOR[0];

    return {
      crewOnCaseHours: Math.round(crewOnCaseHours),
      crewOnNonCaseHours: Math.round(crewOnNonCaseHours),
      crewTotalHours: Math.round(crewTotalHours),
      crewWaitingForCaseHours: Math.round(crewWaitingForCaseHours),
      maintenancesHours: Math.round(maintenancesHours),
      noCrewHours: Math.round(noCrewHours),
      ground: groundShare,
      airGround: airGroundShare,
      airGroundBg: getConicGradient({
        mainColor: tmdxAirGround.main,
        secondaryColor: tmdxAirGround.secondary,
        mainPercents: groundShare,
      }),
      tmdxShare: tmdxSharePercents,
      tmdxShareBg: getConicGradient({
        mainColor: tmdxShareColors.main,
        secondaryColor: tmdxShareColors.secondary,
        mainPercents: tmdxSharePercents,
      }),
      tmdxWaitingForCase,
      tmdxWaitingForCaseBg: getConicGradient({
        mainColor: tmdxWaitingForCaseColors.main,
        secondaryColor: tmdxWaitingForCaseColors.secondary,
        mainPercents: tmdxWaitingForCase,
      }),
      tmdxOnCase,
      tmdxOnCaseBg: getConicGradient({
        mainColor: tmdxOnCaseColors.main,
        secondaryColor: tmdxOnCaseColors.secondary,
        mainPercents: tmdxOnCase,
      }),
      tmdxNonCase,
      tmdxNonCaseBg: getConicGradient({
        mainColor: tmdxNonCaseColors.main,
        secondaryColor: tmdxNonCaseColors.secondary,
        mainPercents: tmdxNonCase,
      }),
      tmdxNoCrew,
      tmdxNoCrewBg: getConicGradient({
        mainColor: tmdxNoCrewColors.main,
        secondaryColor: tmdxNoCrewColors.secondary,
        mainPercents: tmdxNoCrew,
      }),
      tmdxMaintenance,
      tmdxMaintenanceBg: getConicGradient({
        mainColor: tmdxMaintenanceColors.main,
        secondaryColor: tmdxMaintenanceColors.secondary,
        mainPercents: tmdxMaintenance,
      }),
      days,
    };
  }, [statistics]);

  return {
    statistics,
    statisticsData,
  };
};
